/**
 * useWorkspaceData.js
 *
 * Fetches the data of one workspace: the board's widgets are grouped into the
 * minimal set of `GET /api/main-dashboard/summary` requests (one per distinct
 * store + period, asking only for the modules the widgets read), loaded in
 * parallel, with pull-to-refresh and a last-good fallback so a flaky connection
 * never blanks a card that already had numbers.
 *
 * Also exposes {@link useAccessibleModules}: the backend's own module gate, used
 * to offer only widgets the user may actually open.
 *
 * @module helpers/workspaces/useWorkspaceData
 */

import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {fetchMainDashboardModules, fetchMainDashboardSummary} from '../../services/mainDashboardApi';
import {planRequests} from './workspaceData';

/** Last good summary per request key, for the life of the process (stale-while-error). */
const lastGood = new Map();

/** Accessible-module answers per store, with a short TTL. */
const modulesCache = new Map();
const MODULES_TTL_MS = 5 * 60 * 1000;

const signatureOf = (widgets) => (widgets || []).map((w) => `${w.type}:${w.options?.period || ''}:${w.options?.restaurantId || ''}`).join('|');

/**
 * Load the summaries a board needs.
 *
 * @param {?object} board The workspace (widget board).
 * @param {{restaurantId?: ?string, enabled?: boolean}} [options] Default store and an on/off switch.
 * @returns {{summaries: Object.<string, object>, requests: Array, loading: boolean, refreshing: boolean, errors: Object.<string, object>, refresh: () => Promise<void>, lastUpdatedAt: ?number}} Data.
 */
export const useWorkspaceData = (board, {restaurantId = null, enabled = true} = {}) => {
    const widgets = board?.widgets;
    const period = board?.period;
    const signature = signatureOf(widgets);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const requests = useMemo(() => planRequests(widgets, {period, restaurantId}), [signature, period, restaurantId]);
    const [summaries, setSummaries] = useState(() => Object.fromEntries(requests.filter((r) => lastGood.has(r.key)).map((r) => [r.key, lastGood.get(r.key)])));
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
    const runRef = useRef(0);
    const mountedRef = useRef(true);

    useEffect(() => () => {
        mountedRef.current = false;
    }, []);

    const load = useCallback(async (force) => {
        const run = runRef.current + 1;

        runRef.current = run;

        if (!requests.length) {
            setSummaries({});
            setErrors({});
            setLoading(false);

            return;
        }

        const results = await Promise.all(requests.map(async (request) => {
            try {
                const summary = await fetchMainDashboardSummary({
                    period: request.period,
                    modules: request.modules,
                    restaurantId: request.restaurantId,
                    force,
                });

                lastGood.set(request.key, summary);

                return {key: request.key, summary};
            } catch (error) {
                return {key: request.key, error};
            }
        }));

        // A newer load started (tab switched / widgets changed): drop this answer.
        if (!mountedRef.current || runRef.current !== run) {
            return;
        }

        const nextSummaries = {};
        const nextErrors = {};

        results.forEach(({key, summary, error}) => {
            if (summary) {
                nextSummaries[key] = summary;
            } else {
                // Keep showing the last good numbers; flag the failure for the banner.
                if (lastGood.has(key)) {
                    nextSummaries[key] = lastGood.get(key);
                }
                nextErrors[key] = {status: error?.status || 0, message: error?.message || ''};
            }
        });

        setSummaries(nextSummaries);
        setErrors(nextErrors);
        setLastUpdatedAt(Date.now());
    }, [requests]);

    useEffect(() => {
        if (!enabled) {
            return undefined;
        }

        let cancelled = false;

        setLoading(true);
        load(false).finally(() => {
            if (!cancelled && mountedRef.current) {
                setLoading(false);
            }
        });

        return () => {
            cancelled = true;
        };
    }, [load, enabled]);

    const refresh = useCallback(async () => {
        setRefreshing(true);

        try {
            await load(true);
        } finally {
            if (mountedRef.current) {
                setRefreshing(false);
            }
        }
    }, [load]);

    return {summaries, requests, loading, refreshing, errors, refresh, lastUpdatedAt};
};

/**
 * Which dashboard modules the caller may open (backend gate), for the widget library.
 *
 * @param {?string} restaurantId Store id.
 * @returns {{modules: ?Object.<string, boolean>, loading: boolean}} Map `moduleKey -> accessible` (`null` until known / on failure = offer everything, the card itself shows "locked").
 */
export const useAccessibleModules = (restaurantId) => {
    const key = restaurantId || '-';
    const cached = modulesCache.get(key);
    const [modules, setModules] = useState(cached && Date.now() - cached.at < MODULES_TTL_MS ? cached.map : null);
    const [loading, setLoading] = useState(!modules);

    useEffect(() => {
        let cancelled = false;
        const fresh = modulesCache.get(key);

        if (fresh && Date.now() - fresh.at < MODULES_TTL_MS) {
            setModules(fresh.map);
            setLoading(false);

            return undefined;
        }

        setLoading(true);
        fetchMainDashboardModules(restaurantId)
            .then((payload) => {
                const map = {};

                (Array.isArray(payload?.modules) ? payload.modules : []).forEach((item) => {
                    map[item.key] = !!item.accessible;
                });
                modulesCache.set(key, {at: Date.now(), map});
                if (!cancelled) {
                    setModules(map);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setModules(null);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [key, restaurantId]);

    return {modules, loading};
};

export default useWorkspaceData;
